import { useEffect, useState } from "react";
import type { TimedFriendEntry } from "@mk/shared";
import { api } from "../lib/api.ts";
import { messages } from "../i18n/index.ts";
import { UserName } from "./UserName.tsx";

interface TimedFriendsRankingProps {
  /** Değişince liste yeniden çekilir — biten koşu sıralamaya girsin. */
  refreshKey: string;
}

/**
 * Oyuncunun ve arkadaşlarının 3 dakika rekorları. Yalnızca skor paylaşılır;
 * çözülen kelimeler ve tahminler asla.
 */
export function TimedFriendsRanking({ refreshKey }: TimedFriendsRankingProps) {
  const [entries, setEntries] = useState<TimedFriendEntry[] | null>(null);
  const [error, setError] = useState(false);

  useEffect(() => {
    let cancelled = false;
    setError(false);
    api<{ entries: TimedFriendEntry[] }>("/api/solo/timed/friends")
      .then((data) => { if (!cancelled) setEntries(data.entries); })
      .catch(() => { if (!cancelled) setError(true); });
    return () => { cancelled = true; };
  }, [refreshKey]);

  const hasFriends = (entries?.length ?? 0) > 1;

  return (
    <section className="timed-ranking" aria-labelledby="timed-ranking-title">
      <header className="timed-ranking__head">
        <h2 id="timed-ranking-title">{messages.solo.rankingTitle}</h2>
        <span>{messages.solo.rankingHint}</span>
      </header>

      {error && entries === null && <p className="text-sm text-muted">{messages.solo.rankingFailed}</p>}
      {!error && entries === null && <p className="text-sm text-muted">{messages.solo.rankingLoading}</p>}

      {entries && (
        <ol className="flex flex-col gap-1.5">
          {entries.map((entry) => (
            <li key={entry.user.id}>
              <div
                className="daily-friend timed-ranking__row"
                data-status={entry.best ? "solved" : "none"}
                data-you={entry.isYou ? "true" : undefined}
              >
                <span className="daily-friend__score">{entry.rank ?? "—"}</span>
                <span className="daily-friend__name">
                  {entry.isYou ? messages.common.you : <UserName user={entry.user} />}
                </span>
                <span className="daily-friend__status">
                  {entry.best
                    ? messages.solo.rankingScore(entry.best.wordsSolved, entry.best.solvedAttempts)
                    : messages.solo.rankingNotPlayed}
                </span>
              </div>
            </li>
          ))}
        </ol>
      )}

      {entries && !hasFriends && (
        <p className="mt-2 text-sm leading-relaxed text-muted">{messages.solo.rankingEmpty}</p>
      )}
    </section>
  );
}
